import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import {SearchTemplateUserController} from 'src/components/search-template-user';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {
    useTemplateSettings,
} from 'src/modules/profile/account/create-user/steps/users-settings/components/imports-settings';
import {
    ConfirmStep,
    MIGRATION_STEPS,
    MigrationResult,
    STEPS,
    downloadMigrationCsv,
    hasMigrationResult,
    useMigrationPreview,
} from 'src/modules/profile/user/components/migration-stepper';
import {
    migrationStepperStepper,
} from 'src/modules/profile/user/components/migration-stepper/migration-stepper.module.styl';
import {
    ReplaceSearchesSettings,
} from 'src/modules/profile/user/components/replace-search-settings';
import {selectUserDetails} from 'src/modules/profile/user/services/user-page-slice';

import Button from '@pb/kit-component-button';
import {
    Dialog,
    DialogFooter,
    DialogHeader,
    DialogMain,
} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import {Stepper, StepperItem} from '@pb/kit-component-stepper';

import type {ImportContentSubmitParams} from './import-content.component';
import type {UserSearchesReplaceResponse} from 'src/openapi/admin/models/UserSearchesReplaceResponse';
import type {UserShortInfo} from 'src/openapi/admin/models/UserShortInfo';

type Props = {
    userId: number,
    accountId: number,
    shown: boolean,
    onCloseModal: (submit: boolean) => void,
    onSubmitHandler: (params: ImportContentSubmitParams) => Promise<string | null>,
    busy: boolean,
    migrationResult: UserSearchesReplaceResponse | null,
};

export const InnerContentModal = (props: Props) => {
    const {
        userId,
        accountId,
        shown,
        onCloseModal,
        onSubmitHandler,
        busy,
        migrationResult,
    } = props;

    const {shortInfo} = useSelector(selectUserDetails);
    const targetUserName = shortInfo
        ? `${shortInfo.firstName} ${shortInfo.lastName}`
        : '';

    const [currentStep, setCurrentStep] = useState(
        MIGRATION_STEPS.CONFIGURE,
    );
    const [acknowledged, setAcknowledged] = useState(false);
    const [selectedTemplateUser, setSelectedTemplateUser] = useState<
        UserShortInfo | undefined
    >();

    const {
        selectedTemplateUserId,
        setSelectedTemplateUserId,
        savedSearches,
        setSavedSearches,
        templates,
        setTemplates,
        emailAlerts,
        setEmailAlerts,
    } = useTemplateSettings({userId});

    const {userFieldChange} = useEventTracking();

    const isConfirmStep = currentStep === MIGRATION_STEPS.CONFIRM;
    const isConfigure = currentStep === MIGRATION_STEPS.CONFIGURE;
    const isMigrate = currentStep === MIGRATION_STEPS.MIGRATE;

    const {preview, isLoading: previewLoading} = useMigrationPreview({
        userId,
        templateUserId: selectedTemplateUserId ?? userId,
        replaceSearches: savedSearches,
        replaceAlerts: emailAlerts,
        replaceLayouts: templates,
        autoload: isConfirmStep,
    });

    const disabled = useMemo(() => {
        if (!selectedTemplateUserId) {
            return true;
        }
        return !templates && !emailAlerts && !savedSearches;
    }, [selectedTemplateUserId, templates, emailAlerts, savedSearches]);

    useEffect(() => {
        if (shown) {
            setSelectedTemplateUserId(null);
            setSelectedTemplateUser(undefined);
            setCurrentStep(MIGRATION_STEPS.CONFIGURE);
            setAcknowledged(false);
        }
    }, [shown]); // eslint-disable-line react-hooks/exhaustive-deps

    const onCloseHandler = useCallback(() => {
        onCloseModal(false);
    }, [onCloseModal]);

    const onSelectTemplateUser = useCallback(
        (user: UserShortInfo | undefined) => {
            setSelectedTemplateUser(user);
            setSelectedTemplateUserId(user?.userId ?? null);
        },
        [setSelectedTemplateUserId],
    );

    const onContinue = useCallback(() => {
        setCurrentStep(MIGRATION_STEPS.CONFIRM);
    }, []);

    const onBack = useCallback(() => {
        setAcknowledged(false);
        setCurrentStep(MIGRATION_STEPS.CONFIGURE);
    }, []);

    const onSubmit = useCallback(async () => {
        if (!selectedTemplateUserId) {
            return;
        }

        const error = await onSubmitHandler({
            templateUserId: selectedTemplateUserId,
            replaceSearches: savedSearches,
            replaceAlerts: emailAlerts,
            replaceLayouts: templates,
        });

        if (!error) {
            setCurrentStep(MIGRATION_STEPS.MIGRATE);
        }
    }, [
        selectedTemplateUserId,
        savedSearches,
        emailAlerts,
        templates,
        onSubmitHandler,
    ]);

    const onDone = useCallback(() => {
        onCloseModal(true);
    }, [onCloseModal]);

    const onDownloadReport = useCallback(() => {
        if (migrationResult) {
            downloadMigrationCsv(
                migrationResult,
                'import_content_report.csv',
            );
        }
    }, [migrationResult]);

    const templateUserName = selectedTemplateUser
        ? `${selectedTemplateUser.firstName} ${selectedTemplateUser.lastName}`
        : '';

    const onFieldChange = useCallback((parameterName: string) => {
        userFieldChange({
            parameterName,
            viewType: 'Existed',
            section: 'Import content & layouts',
        });
    }, [userFieldChange]);

    let dialogWidth = 450;
    if (!isConfigure) {
        dialogWidth = 850;
    }

    return (
        <Dialog
            shown={shown}
            setShown={onCloseHandler}
            wrapperProps={{style: {width: dialogWidth}}}>
            <DialogHeader onClose={onCloseHandler}>
                Replace User Data & Settings
            </DialogHeader>
            <DialogMain>
                <div className={migrationStepperStepper}>
                    <Stepper activeStep={currentStep}>
                        {STEPS.map(step => (
                            <StepperItem
                                key={step.step}
                                caption={step.caption}
                            />
                        ))}
                    </Stepper>
                </div>

                {isConfigure ? (
                    <Group
                        direction="v"
                        style={{padding: 24}}>
                        <SearchTemplateUserController
                            onSelect={onSelectTemplateUser}
                            accountId={accountId}
                            onFieldChange={onFieldChange}
                        />
                        <ReplaceSearchesSettings {...{
                            onFieldChange,
                            setSavedSearches,
                            savedSearches,
                            templates,
                            setTemplates,
                            emailAlerts,
                            setEmailAlerts,
                        }}
                        />
                    </Group>
                ) : null}

                {isConfirmStep ? (
                    <ConfirmStep
                        preview={preview}
                        previewLoading={previewLoading}
                        acknowledged={acknowledged}
                        onAcknowledgedChange={setAcknowledged}
                        hasTemplateUser={selectedTemplateUserId !== null}
                        hasContentSelected={
                            savedSearches || emailAlerts || templates
                        }
                        currentUserContent={
                            preview?.currentUserState ?? null
                        }
                    />
                ) : null}

                {isMigrate ? (
                    <MigrationResult
                        migrationResult={migrationResult}
                        userName={targetUserName}
                        templateUserName={templateUserName}
                        accountName=""
                        hasTemplateUser={true}
                        onDownload={onDownloadReport}
                        downloadDisabled={
                            !hasMigrationResult(migrationResult)
                        }
                    />
                ) : null}
            </DialogMain>
            <DialogFooter>
                {isConfigure ? (
                    <Group>
                        <Button
                            variant="Type.PRIMARY"
                            disabled={disabled}
                            onClick={onContinue}>
                            Continue
                        </Button>
                        <Button
                            variant="Type.SECONDARY"
                            onClick={onCloseHandler}>
                            Cancel
                        </Button>
                    </Group>
                ) : null}

                {isConfirmStep ? (
                    <Group>
                        <Button
                            variant="Type.SECONDARY"
                            onClick={onBack}>
                            Back
                        </Button>
                        <Button
                            variant="Type.PRIMARY"
                            disabled={!acknowledged}
                            busy={busy}
                            onClick={onSubmit}>
                            Start Migration
                        </Button>
                    </Group>
                ) : null}

                {isMigrate ? (
                    <Button
                        variant="Type.PRIMARY"
                        onClick={onDone}>
                        Done
                    </Button>
                ) : null}
            </DialogFooter>
        </Dialog>
    );
};
